import { SearchData, SortData } from "#modules/quoteRequestLines/fetchQuoteRequestLines.api";
import { FilterQuery, QuoteRequestStatusType, SearchEntity } from "shared";

export const queryKeys = {
  parts: {
    all: ["part"],
    detail: (id: string | undefined) => [...queryKeys.parts.all, id],
    withChildren: (id: string | undefined) => [...queryKeys.parts.all, id, "children"],
    withQuoteRequestLines: (id: string | undefined) => [
      ...queryKeys.parts.all,
      id,
      "quoteRequestLines",
    ],
    json: (id: string | undefined) => ["partJson", id],
    features: (id: string | undefined) => ["parts", id, "features"],
    weldLength: (id: string | undefined) => ["parts", id, "weldLength"],
    featuresBulk: (partIds: string[]) => [
      "parts",
      "bulk",
      "features",
      partIds.toSorted().join(","),
    ],
    search: (filtersParams: FilterQuery, limit: number) => [
      "parts",
      "search",
      limit,
      filtersParams,
    ],
    topology: (
      id: string | undefined,
      filterParams: FilterQuery,
      limit: number,
      shouldReturnChildrenOfAssembly?: boolean,
      dimensionWeight?: number,
    ) => [
      "searchTopology",
      id,
      limit,
      filterParams,
      shouldReturnChildrenOfAssembly,
      dimensionWeight,
    ],
  },
  quoteRequestLines: {
    all: ["quoteRequestLines"],
    detail: (id: string | undefined) => [...queryKeys.quoteRequestLines.all, id],
    reliables: (id: string | undefined, filterParams: FilterQuery, dimensionWeight: number) => [
      "reliables",
      ...queryKeys.quoteRequestLines.detail(id),
      filterParams,
      dimensionWeight,
    ],
    customInputs: (id: string | undefined) => [
      ...queryKeys.quoteRequestLines.detail(id),
      "customInputs",
    ],
    overthickness: (id: string | undefined) => [
      ...queryKeys.quoteRequestLines.detail(id),
      "overthickness",
    ],
    bulk: (ids: string[]) => [...queryKeys.quoteRequestLines.all, "bulk", ids.toSorted().join(",")],
    search: (page = 1, search?: SearchData, sort?: SortData) => {
      const key: (string | number | object)[] = ["quoteRequestLinesSearch"];
      if (page) {
        key.push({ page });
      }
      if (search) {
        key.push({ search });
      }
      if (sort) {
        key.push({ sort });
      }
      return key;
    },
  },
  quoteRequests: {
    all: ["quoteRequests"],
    v2: {
      all: ["quoteRequests", "v2"],
      search: (page: number, perPage: number) => [...queryKeys.quoteRequests.v2.all, page, perPage],
    },
    detail: (id: string | undefined) => [...queryKeys.quoteRequests.all, id],
    quotes: (id: string | undefined) => [...queryKeys.quoteRequests.detail(id), "quotes"],
    weight: (id: string | undefined) => [...queryKeys.quoteRequests.detail(id), "weight"],
    consultationExport: (id: string, exportId: string) => [
      ...queryKeys.quoteRequests.detail(id),
      "consultationExport",
      exportId,
    ],
    transport: (id: string | undefined) => [...queryKeys.quoteRequests.detail(id), "transport"],
    processSteps: (id: string | undefined) => [
      ...queryKeys.quoteRequests.detail(id),
      "processSteps",
    ],
    customInputsBulk: (id: string | undefined) => [
      ...queryKeys.quoteRequests.detail(id),
      "customInputs",
    ],
    list: (
      page = 1,
      filters?: {
        status?: QuoteRequestStatusType;
        ownerId?: string;
        isFavorite?: boolean;
      },
    ) => {
      const key: (string | number | object)[] = ["quoteRequests"];
      if (page) {
        key.push(page);
      }
      if (filters) {
        key.push(filters);
      }
      return key;
    },
  },
  quoteRequestLineActivePrediction: {
    detail: (id: string | undefined) => ["quoteRequestLineActivePrediction", id],
  },
  surfaceTreatmentsPredictions: {
    detail: (quoteRequestLineId: string | undefined) => [
      "surfaceTreatmentsPredictions",
      quoteRequestLineId,
    ],
  },
  customInputsCatalog: {
    all: ["customInputs"],
  },
  address: {
    all: ["address"],
    detail: (id: string | undefined) => [...queryKeys.address.all, id],
  },
  clients: {
    all: ["clients"],
    detail: (id: string | undefined) => [...queryKeys.clients.all, id],
  },
  clusters: {
    all: ["clusters"],
    detail: (id: string | undefined) => [...queryKeys.clusters.all, id],
  },
  company: {
    mine: ["company"],
  },
  customSurfaceTreatments: {
    all: ["customSurfaceTreatments"],
    detail: (companyId: string | undefined) => [
      ...queryKeys.customSurfaceTreatments.all,
      companyId,
    ],
  },
  contacts: {
    all: ["contacts"],
    detail: (id: string | undefined) => [...queryKeys.contacts.all, id],
  },
  layoutNestings: {
    all: ["layoutNestings"],
    detail: (layoutId: string | undefined | null) => [...queryKeys.layoutNestings.all, layoutId],
  },
  layouts: {
    all: ["layouts"],
    detail: (id: string | undefined | null) => [...queryKeys.layouts.all, id],
  },
  companyMachines: {
    all: ["companyMachines"],
  },
  customRecommendations: {
    all: ["customRecommendations"],
    detail: (id: string) => [...queryKeys.customRecommendations.all, id],
  },
  materials: {
    all: ["materials"],
    custom: ["materials", "custom"],
    volumetricPrice: ["materials", "volumetricPrice"],
    structureImport: (jobId: string) => ["materials", "structureImport", jobId],
  },
  models: {
    all: ["models"],
    mine: ["models", "mine"],
    mineVersion: ["models", "mineVersion"],
  },
  onboardingProcessSteps: {
    all: ["onboardingProcessSteps"],
  },
  optimizations: {
    all: ["optimizations"],
    byQuoteRequest: (quoteRequestId: string | undefined) => [
      ...queryKeys.optimizations.all,
      quoteRequestId,
    ],
    enabledByQuoteRequest: (quoteRequestId: string | undefined) => [
      ...queryKeys.optimizations.all,
      "enabled",
      quoteRequestId,
    ],
    lineRemovalImpact: (lineIds: string[]) => [
      ...queryKeys.optimizations.all,
      "line-removal-impact",
      ...[...lineIds].sort(),
    ],
  },
  permissions: {
    all: ["permissions"],
  },
  predictionFeedback: {
    all: ["predictionFeedback"],
    detail: (id: string | undefined) => [...queryKeys.predictionFeedback.all, id],
  },
  products: {
    all: ["products"],
  },
  processStepCatalog: {
    all: ["processStepCatalog"],
  },
  similarPartFeedbacks: {
    all: ["similarPartFeedbacks"],
    bySourcePart: (sourcePartId: string | undefined) => [
      ...queryKeys.similarPartFeedbacks.all,
      sourcePartId,
    ],
  },
  searchQuoteRequestLineSimilarParts: {
    all: ["searchQuoteRequestLineSimilarParts"],
    detail: (id: string | undefined, limit: number, filterParams: FilterQuery) => [
      ...queryKeys.searchQuoteRequestLineSimilarParts.all,
      id,
      limit,
      filterParams,
    ],
  },
  outlookAccount: {
    mine: ["outlookAccount"],
  },
  team: {
    mine: ["team"],
  },
  user: {
    mine: ["user"],
  },
  search: {
    recent: () => ["search", "recent"],
    results: (query: string, entity: SearchEntity | undefined, page: number) => [
      "search",
      "results",
      query,
      entity ?? "all",
      page,
    ],
  },
};
